import { cubicEaseOut, transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { h1 } from '@helpers/typography';
import SectionWrapper from '@stories/Components/Global/SectionWrapper/SectionWrapper';
import styled, { css } from 'styled-components';
const Container = styled(SectionWrapper) `
  --textColor: var(--bgColor);
  --blur: 0px;
  --tint: 0.5;
  --indicatorSize: 72px;

  position: relative;
  color: var(--textColor);
  overflow: hidden;

  @media ${until(Device.DesktopSmall)} {
    margin-top: -17px; 
  }
`;
const List = styled.div `
  display: grid;
`;
const Item = styled.div `
  ${transition('opacity')};

  grid-column: 1;
  grid-row: 1;

  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;
  }

  &[data-active='true'] {
    opacity: 1;
  }
`;
const Inner = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  min-height: calc(100vh - var(--headerHeight) - var(--indicatorSize));
  padding: 85px 0;
  gap: ${fluid(24, 32)};

  @media ${from(Device.TabletSmall)} {
    padding: 54px 0;
  }
`;
const TitleArea = styled.div ``;
const Tagline = styled.span `
  ${fonts.cabin.bold};
  font-size: ${fluid(16, 24)};
  line-height: ${fluid(20, 44)};
  margin-bottom: 10px;
`;
const Title = styled.h1 `
  ${h1};
  font-size: ${fluid(32, 48)};
  line-height: ${fluid(36, 54)};

  margin: 0;

  @media ${from(Device.TabletSmall)} {
    max-width: 24ch;
  }
`;
const Intro = styled.p `
  ${fonts.cabin.regular};
  font-weight: 400;
  color: currentColor;
  margin: ${fluid(24, 36)} 0 0;
  font-size: ${fluid(15, 24)};
  line-height: ${fluid(25, 34)};

  @media ${from(Device.TabletSmall)} {
    max-width: 40ch;
  }
`;
const CtaWrapper = styled.div ``;
const ImageWrapper = styled.div `
  position: absolute;
  top: calc(var(--blur) * -4);
  left: calc(var(--blur) * -4);
  height: calc(100% + (var(--blur) * 8));
  filter: blur(var(--blur)) brightness(calc(1 - var(--tint)));
  margin: calc(var(--blur) * -1);
  width: calc(100% + (var(--blur) * 8));
  z-index: -1;
`;
const Image = styled.img `
  ${imageCoverContainer()};
`;
const Video = styled.video `
  ${imageCoverContainer()};
`;
const BottomWrapper = styled.div `
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: var(--indicatorSize);
  width: 100%;
`;
const PlayPaths = css `
  &::before {
    clip-path: polygon(0 0, 50% 25%, 50% 75%, 0% 100%);
  }

  &::after {
    clip-path: polygon(50% 25%, 100% 50%, 100% 50%, 50% 75%);
  }
`;
const PausePaths = css `
  &::before {
    clip-path: polygon(0 0, 30% 0, 30% 100%, 0% 100%);
  }

  &::after {
    clip-path: polygon(70% 0, 100% 0, 100% 100%, 70% 100%);
  }
`;
const PlayPauseButtonWrapper = styled.div `
  position: relative;
  width: 24px;
  height: 24px;
  align-self: center;
  padding: 3px 0 3px 6px;
`;
const PlayPauseButton = styled.button `
  ${transition('opacity')};
  ${ButtonReset};

  will-change: opacity;
  position: relative;
  opacity: 1;
  width: 100%;
  height: 100%;

  &::before,
  &::after {
    ${transition('clip-path left right')};

    display: block;
    content: '';
    width: 100%;
    height: 100%;
    background: var(--textColor);
    position: absolute;
    top: 0;
  }

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
  }

  &.focus-ring {
    outline: auto;
    outline-color: currentColor;
    outline-offset: 3px;
    outline-width: thin;
  }

  &.focus-ring,
  &:hover {
    opacity: 0.7;
  }

  &[data-paused='false'] {
    ${PausePaths};
  }

  &[data-paused='true'] {
    ${PlayPaths};
  }
`;
const ScrollIndicator = styled.div `
  font-weight: 400;

  @keyframes growIndicator {
    0% {
      max-width: 0;
    }
    80% {
      max-width: var(--indicatorSize);
    }
  }

  position: relative;
  width: var(--indicatorSize);
  padding-bottom: 6px;
  transform: rotate(90deg) translateX(-100%);
  transform-origin: bottom left;

  &::after {
    animation: growIndicator 1200ms ${cubicEaseOut};
    animation-iteration-count: infinite;
    animation-direction: alternate;
    content: '';
    display: block;
    height: 2px;
    background-color: var(--textColor);
    width: 100%;
    position: absolute;
    bottom: 0;
    left: 0;
  }
`;
const Controls = styled.div `
  display: flex;
  gap: 28px;
  padding-bottom: 25px;
`;
const ControlButton = styled.button `
  ${ButtonReset};
  ${transition('opacity')};

  width: 30px;
  height: 20px;
  color: var(--textColor);

  > svg {
    ${transition('transform')};
    height: 100%;
    width: 100%;
  }

  &:first-of-type {
    transform: rotate(180deg);
  }

  &.focus-ring {
    outline: auto;
    outline-color: currentColor;
    outline-offset: 3px;
    outline-width: thin;
  }

  &[disabled],
  &.focus-ring,
  &:hover {
    opacity: 0.5;
    cursor: pointer;
  }

  &:not([disabled]) {
    &.focus-ring,
    &:hover {
      > svg {
        transform: translateX(15%);
      }
    }
  }
`;
const Overlay = styled.div `
  ${siteWide};

  height: 100%;
  position: relative;

  &::before {
    content: '';
    width: 50%;
    height: 100%;
    display: block;
    position: absolute;
    background-color: hsla(var(--bgColorString) / 0.2);
    transform: translateX(50%) skewX(-26.65deg);
    top: 0;
    right: 0;

    @media ${from(Device.Tablet)} {
      width: 27.5%;
      transform: skewX(-26.65deg);
    }
  }
`;
const LandingPageHeroStyles = {
    BottomWrapper,
    Container,
    ControlButton,
    Controls,
    CtaWrapper,
    Image,
    ImageWrapper,
    Inner,
    Intro,
    Item,
    List,
    Overlay,
    PlayPauseButton,
    PlayPauseButtonWrapper,
    ScrollIndicator,
    Tagline,
    Title,
    TitleArea,
    Video,
};
export default LandingPageHeroStyles;
